import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';

import { ReactComponent as DocumentFileIcon } from '../../assets/icons/document.svg';
import { ReactComponent as EyeIcon } from '../../assets/icons/eye-fill-icon.svg';
import { useAppBranding } from '../../config/branding';
import { AvailableStation } from '../../interface/station-management.interface';
import { useAppDispatch } from '../../redux/hooks';
import { setNotificationDetails } from '../../redux/slice/NotificationSlice';
import { convertTo12HourFormat } from '../../shared-functions/DataTime';
import chargingStation from '../../utils/api/station';
import BatteryLoader from '../battery-loader/BatteryLoader';
import CustomTable from '../custom-table/CustomTable';
import ModalBox from '../modal-box/ModalBox';

interface StationSidePanelProperties {
  fetchIdFrom: 'queryParams' | 'props';
  id?: string;
}

interface ChargingPointProperties {
  chargingPointSn: string;
  connectorType: string; // use union if more types exist
  isDeviceOffline: boolean;
  _id: string;
  id: string;
}

/**
 * View the file based on its type
 * @param param0
 * @returns
 */
function FilePreview({ file, extraCount = 0 }: { file: any; extraCount?: number }) {
  const { t } = useTranslation('tableSidepanel');

  let fileTypeView;

  if (file.fileType.includes('image')) {
    fileTypeView = <img src={file.signedUrl} alt="" className="selected-image" />;
  } else if (file.fileType.includes('video')) {
    fileTypeView = (
      <video controls className="selected-video">
        <source src={file.signedUrl} type={file.fileType} />
        <track src="path/to/captions.vtt" kind="captions" label="English captions" default />
        {t('browserNotVideoTag')}{' '}
      </video>
    );
  }

  return (
    <div className="single-selected-wrap">
      {fileTypeView}
      {extraCount && extraCount > 0 ? (
        <div className="extra-count-overlay">
          <span>+{extraCount}</span>
        </div>
      ) : null}
    </div>
  );
}
FilePreview.defaultProps = {
  extraCount: 0,
};

/**
 * Station side panel
 * @param properties
 * @returns
 */
function StationSidePanel(properties: StationSidePanelProperties) {
  const branding = useAppBranding();
  const { t, i18n } = useTranslation('tableSidepanel');
  const { fetchIdFrom, id } = properties;
  const { status: parameterStatus, page } = useParams();
  const status = parameterStatus || page;
  const dispatch = useAppDispatch();
  const [searchParameters] = useSearchParams();
  const [chargingStationData, setChargingStationData] = useState<AvailableStation>();
  const [chargingPointData, setChargingPointData] = useState<ChargingPointProperties[]>([]);
  const [isLoader, setIsLoader] = useState<boolean>(false);
  const [isDocumentModalOpen, setIsDocumentModalOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<{
    url: string;
    type: string;
    name: string;
  } | null>(null);

  /**
   * Handle view document
   */
  const handleViewDocument = (file: any) => {
    if (file?.signedUrl) {
      setSelectedDocument({
        url: file.signedUrl,
        type: file.fileType || 'application/pdf', // Fallback or logic
        name: file.fileName || 'Document',
      });
      setIsDocumentModalOpen(true);
    }
  };

  /**
   * Render operating hours in a 3-column grid
   */
  const renderOperatingHoursGrid = (hours: any | undefined) => {
    if (!hours) return <p className="basic-info-value">N/A</p>;

    const days = [
      'monday',
      'tuesday',
      'wednesday',
      'thursday',
      'friday',
      'saturday',
      'sunday',
    ] as const;

    return (
      <div className="opening-hours-grid">
        {days.map((day) => {
          const dayData = hours[day];
          return (
            <div key={day} className="day-grid-item">
              <p className="day-label">{t(day)}</p>
              {dayData?.isOpen || dayData?.status === 'open' ? (
                <div className="time-range">
                  <span>
                    {dayData.from || dayData.open
                      ? convertTo12HourFormat(dayData.from || dayData.open)
                      : 'N/A'}{' '}
                    -
                  </span>
                  <span>
                    {dayData.to || dayData.close
                      ? convertTo12HourFormat(dayData.to || dayData.close)
                      : 'N/A'}
                  </span>
                </div>
              ) : (
                <div className="day-closed">
                  <p className="closed-text">{t('closed')}</p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const basicInfoDetails = [
    {
      label: t('electricityType'),
      value: 'electricityContractType' as keyof AvailableStation,
    },
    {
      label: t('timeZone'),
      value: 'time_zone' as keyof AvailableStation,
    },
    {
      label: t('openingHours'),
      value: 'operatingHours' as keyof AvailableStation,
      /**
       * Function to get custom data
       * @param row
       * @returns
       */
      customData: (row: AvailableStation) => renderOperatingHoursGrid(row?.operatingHours),
    },
    {
      label: t('addressLabel'),
      value: 'address' as keyof AvailableStation,
    },
  ];

  const amenitiesDetails = [
    {
      name: t('gasStation'),
      key: 'Gas Station',
      svgIconActive: <branding.icons.GasStationActive />,
    },
    {
      name: t('wifi'),
      key: 'Free Wifi',
      svgIconActive: <branding.icons.WifiActive />,
    },
    {
      name: t('restroom'),
      key: 'Restroom',
      svgIconActive: <branding.icons.RestroomActive />,
    },
    {
      name: t('lounge'),
      key: 'Lounge',
      svgIconActive: <branding.icons.LoungeActive />,
    },
    {
      name: t('cafe'),
      key: 'Cafe',
      svgIconActive: <branding.icons.CaffeeActive />,
    },
    {
      name: t('shopping'),
      key: 'Shopping',
      svgIconActive: <branding.icons.ShoppingActive />,
    },
    {
      name: t('restaurant'),
      key: 'Restaurant',
      svgIconActive: <branding.icons.RestaurantActive />,
    },
    {
      name: t('relaxingArea'),
      key: 'Relaxing Area',
      svgIconActive: <branding.icons.RelaxingActive />,
    },
    {
      name: t('carWash'),
      key: 'Car Wash',
      svgIconActive: <branding.icons.CarwashActive />,
    },
  ];

  const column = [
    {
      label: t('device'),
      accessorKey: 'chargingPointSn' as keyof ChargingPointProperties,
      sortAllow: false,
    },
    {
      label: t('status'),
      accessorKey: 'isDeviceOffline' as keyof ChargingPointProperties,
      sortAllow: false,
      /**
       * Function
       * @param row
       * @returns
       */
      customData: (row: ChargingPointProperties) => (
        <p>{row.isDeviceOffline ? 'Offline' : 'Online'}</p>
      ),
    },
    {
      label: t('connectorType'),
      accessorKey: 'connectorType' as keyof ChargingPointProperties,
      sortAllow: false,
    },
  ];

  /**
   * Function to fetch station details
   * @param stationId
   */
  const handleFetchStationDetails = async (stationId: string) => {
    setIsLoader(true);
    chargingStation
      .fetchChargingStationByStationId(stationId)
      .then((response) => {
        setChargingStationData(response?.data);
        setIsLoader(false);
      })
      .catch((error) => {
        setIsLoader(false);
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 200,
              message: error?.response?.data?.message || 'Something went wrong!',
            },
          }),
        );
      });
  };

  /**
   * Function to fetch station details
   * @param stationId
   */
  const handleFetchDeviceDetails = async (stationId: string) => {
    chargingStation
      .fetchChargingPointByStationId(stationId)
      .then((response) => {
        setChargingPointData(response.data);
      })
      .catch((error) => {
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 200,
              message: error?.response?.data?.message || 'Something went wrong!',
            },
          }),
        );
      });
  };

  /**
   * Function to render document chip
   * @param param0 - DocumentChipProps
   * @returns JSX.Element
   */
  function DocumentChip({
    label,
    file = undefined,
    onClick,
  }: {
    label: string;
    file?: any;
    onClick: () => void;
  }) {
    return (
      <div className="document-chip-wrap">
        <span className="doc-label">{label === 'ebReports' ? t('ebReports') : label}</span>
        <div className="doc-file-box">
          <div className="doc-icon-name">
            <DocumentFileIcon />
            <span className="file-name">{file ? file.fileName : t('noFile')}</span>
          </div>
          {file && (
            <div className="doc-view-icon" role="presentation" onClick={onClick}>
              <EyeIcon />
            </div>
          )}
        </div>
      </div>
    );
  }
  DocumentChip.defaultProps = {
    file: undefined,
  };

  /**
   * Handle close document modal
   */
  const handleCloseDocumentModal = () => {
    setIsDocumentModalOpen(false);
    setSelectedDocument(null);
  };

  useEffect(() => {
    let stationId;
    if (fetchIdFrom === 'queryParams') {
      stationId = searchParameters.get('stationId');
    } else if (fetchIdFrom === 'props') {
      stationId = id;
    }

    if (!stationId) {
      return;
    }

    handleFetchStationDetails(stationId);
    handleFetchDeviceDetails(stationId);
  }, [searchParameters, id]);

  if (isLoader) {
    return (
      <div className="loader-wrap">
        <div className="loader">
          <BatteryLoader />
        </div>
      </div>
    );
  }

  return (
    <div className="station-side-panel-wrap" dir={i18n.dir()}>
      {/* <ModalBox
        open={modalBoxStatus === 'open'}
        title={modalBoxDetail.title}
        content={modalBoxDetail.content}
        closeModal={() => setModalBoxStatus('close')}
        header={false}
      /> */}
      <ModalBox
        title={selectedDocument?.name || 'Document Preview'}
        open={isDocumentModalOpen}
        closeModal={handleCloseDocumentModal}
        header
        position="center"
        content={
          <div className="document-preview-container">
            {(() => {
              if (!selectedDocument?.url)
                return <p className="document-preview-text">No document to display</p>;

              if (selectedDocument.type.includes('image')) {
                return (
                  <img
                    src={selectedDocument.url}
                    alt="Document"
                    className="document-preview-image"
                  />
                );
              }

              return (
                <iframe
                  src={selectedDocument.url}
                  title="Document Viewer"
                  className="document-preview-iframe"
                />
              );
            })()}
          </div>
        }
      />
      <div className="station-details-header">
        <p className="title">{t('stationDetails')}</p>
      </div>
      <div className="station-data-wrapper">
        <div className="station-content-wrap">
          <div className="station-content-header">
            <h2 className="station-name">{chargingStationData?.name ?? 'N/A'}</h2>

            <p className="station-address">
              {chargingStationData?.facilityTagData?.[0]?.facilityTagName ?? 'N/A'}
            </p>
          </div>
          <div className="divider" />
          <div className="station-basic-details">
            <p className="title">{t('basicInformation')}</p>

            {basicInfoDetails.map((item) => (
              <div key={item.label} className="basic-info-item">
                <p className="basic-info-label">{item.label}:</p>
                {item.customData ? (
                  item.customData(chargingStationData!)
                ) : (
                  <p className="basic-info-value">
                    {String(chargingStationData?.[item.value] ?? 'N/A')}
                  </p>
                )}
              </div>
            ))}
          </div>
          <div className="divider" />
          <div className="station-basic-details">
            <p className="title">{t('documents')}</p>
            <div className="documents-row">
              <DocumentChip
                label={t('landDocument')}
                file={chargingStationData?.landOwnerShipDocument?.[0]}
                onClick={() => handleViewDocument(chargingStationData?.landOwnerShipDocument?.[0])}
              />
              <DocumentChip
                label={t('ebReports')}
                file={chargingStationData?.ebReport?.[0]}
                onClick={() => handleViewDocument(chargingStationData?.ebReport?.[0])}
              />
            </div>
          </div>
          {status === 'rejected' && (
            <div className="station-status">
              <p className="title">{t('stationVerification')}</p>
              <div className="reason">
                <p className="reason-text">
                  {t('rejectedReason')}:{' '}
                  <span>{chargingStationData?.submission?.reason || 'N/A'}</span>
                </p>
              </div>
            </div>
          )}
        </div>
        {/* Media */}
        <div className="section-block media-block">
          <p className="title">{t('mediaAmenities')}</p>
          {/* VIEW MODE MEDIA */}
          <div className="media-grid-view">
            {chargingStationData?.medias?.slice(0, 4).map((media, index) => {
              const remaining = (chargingStationData?.medias?.length || 0) - 4;
              return (
                <FilePreview
                  key={media.url}
                  file={media}
                  extraCount={index === 3 ? remaining : 0}
                />
              );
            })}
          </div>

          {/* AMENITIES VIEW */}
          {chargingStationData?.amenities?.length ? (
            <>
              <div className="divider" />
              <div className="amenities-container">
                <p className="title">{t('amenities')}</p>

                <div className="amenities-list">
                  {chargingStationData?.amenities?.map((each: any) => {
                    const amenityToShow =
                      amenitiesDetails.find((a) => a.key === each) || amenitiesDetails[0];

                    return (
                      <div className="single-amenity-item" key={each}>
                        <div className="icon-box">{amenityToShow.svgIconActive}</div>
                        <span>{amenityToShow.name}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          ) : null}
        </div>
      </div>

      {/* ===================== DEVICE INFO (BOTTOM) ===================== */}
      <div className="table-container">
        <p className="heading">{t('deviceInfo')}</p>

        <CustomTable
          serialNo
          header={column}
          data={chargingPointData}
          loading={false}
          filterAllow={false}
          sizeChangeButtonRequired={false}
          limit={0}
          currentPage={0}
          totalPages={0}
          topHeader={false}
          totalResults={0}
          handlePageChange={() => {}}
          handleLimitChange={() => {}}
          leftTopContent={<p className="device-title">{t('deviceInfo')}</p>}
        />
      </div>
    </div>
  );
}

export default StationSidePanel;
